import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CITY_LAUNCH_RADIUS_MILES } from "@/lib/cityLaunchRadius";

/**
 * The one radius the coverage question is asked at. Printed in the copy that
 * reports the answer, so the sentence can never drift from the query.
 */
export const COVERAGE_RADIUS_MILES = CITY_LAUNCH_RADIUS_MILES;

/**
 * Probes the `list-cafes` edge function at COVERAGE_RADIUS_MILES around a
 * searched point to answer a single yes/no question: is there ANY café in
 * Grounded's coverage close enough to this location to be worth going to?
 *
 * Powers the "Grounded isn't in your city yet" empty state on Browse. Enabled
 * only when the caller has already established a true zero-result condition at
 * the current radius. Note this is deliberately NOT an upper bound over every
 * search circle Browse can use: at 10 miles "covered" means cafés someone could
 * actually reach, so a place whose nearest café is further out now reports
 * uncovered and gets the waitlist card.
 *
 * `hasCoverageNearby` is `null` while unknown so consumers can keep rendering
 * their existing empty state until the probe resolves.
 */
export function useAreaCoverageProbe({
  lat,
  lng,
  enabled,
}: {
  lat: number | null | undefined;
  lng: number | null | undefined;
  enabled: boolean;
}): { hasCoverageNearby: boolean | null; isChecking: boolean } {
  const canRun = enabled && typeof lat === "number" && typeof lng === "number";

  const query = useQuery({
    queryKey: ["area-coverage-probe", lat, lng, COVERAGE_RADIUS_MILES],
    enabled: canRun,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/list-cafes`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            lat,
            lng,
            radius_miles: COVERAGE_RADIUS_MILES,
            limit: 1,
          }),
        },
      );
      if (!res.ok) throw new Error(`list-cafes probe failed: ${res.status}`);
      const json = await res.json();
      const total = typeof json?.total === "number" ? json.total : (json?.cafes?.length ?? 0);
      // Hardening: list-cafes returns total: 0 if its internal count query errors even when cafés exist.
      return total > 0 || ((json?.cafes?.length ?? 0) > 0);
    },
  });

  if (!canRun) return { hasCoverageNearby: null, isChecking: false };
  if (query.isLoading || query.isFetching) return { hasCoverageNearby: null, isChecking: true };
  if (query.isError) return { hasCoverageNearby: null, isChecking: false };
  return { hasCoverageNearby: query.data ?? null, isChecking: false };
}
